import React from 'react';
import PageLayout from 'components/PageLayout';
import LoadingState from 'components/LoadingState';
import ErrorState from 'components/ErrorState';
import SubmissionForm from 'components/SubmissionForm';
import SubmissionClosedState from 'components/SubmissionClosedState';
import AccessDenied from 'components/AccessDenied';
import { useSubmissionForm } from 'hooks/useSubmissionForm';
import { isSubmissionClosed, getCloseDate } from 'utils/submissionDate';
import { useRoleInfo } from 'hooks/useRoleInfo';

interface SMDDFormPageProps {
  isAdmin?: boolean;
}

const SMDDFormPage: React.FC<SMDDFormPageProps> = ({ isAdmin = false }) => {
  const { userIsSuperAdmin, userIsPillarPIC } = useRoleInfo();
  const {
    selectedCategory,
    submitted,
    error,
    isSubmitting,
    rubricData,
    categories,
    handleCategoryChange,
    handleFormSubmit,
    dynamicFormSchema,
    progressStep,
  } = useSubmissionForm();

  const closed = !isAdmin && isSubmissionClosed();
  const closeDate = getCloseDate();

  if (isAdmin && !userIsSuperAdmin && !userIsPillarPIC) {
    return (
      <PageLayout
        title="Admin Access Required"
        subtitle="This page is restricted to administrators and PICs only."
      >
        <AccessDenied
          title="Access Restricted"
          message="You must be logged in as an administrator or Pillar PIC to access the bypass form."
          buttonLink="/"
          buttonText="Return to Home"
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Submit Your Application"
      subtitle="Share your digital excellence story and join Indonesia's most prestigious internal awards."
    >
      {/* Progress Wizard - Transparent on gray background */}
      {!closed && (
        <div className="flex justify-center items-center space-x-8 py-6 mb-8">
          <div className="flex items-center">
            <div
              className={`w-10 h-10 ${
                progressStep >= 1
                  ? 'bg-black text-white'
                  : 'bg-gray-300 text-gray-600'
              } rounded-full flex items-center justify-center text-sm font-bold transition-all`}
            >
              1
            </div>
            <span
              className={`ml-3 text-sm ${
                progressStep >= 1 ? 'text-gray-900' : 'text-gray-500'
              } font-baijamjuree font-medium`}
            >
              Category
            </span>
          </div>
          <div className="w-12 h-px bg-gray-300"></div>
          <div className="flex items-center">
            <div
              className={`w-10 h-10 ${
                progressStep >= 2
                  ? 'bg-black text-white'
                  : 'bg-gray-300 text-gray-600'
              } rounded-full flex items-center justify-center text-sm font-bold transition-all`}
            >
              2
            </div>
            <span
              className={`ml-3 text-sm ${
                progressStep >= 2 ? 'text-gray-900' : 'text-gray-500'
              } font-baijamjuree font-medium`}
            >
              Details
            </span>
          </div>
          <div className="w-12 h-px bg-gray-300"></div>
          <div className="flex items-center">
            <div
              className={`w-10 h-10 ${
                progressStep >= 3
                  ? 'bg-black text-white'
                  : 'bg-gray-300 text-gray-600'
              } rounded-full flex items-center justify-center text-sm font-bold transition-all`}
            >
              3
            </div>
            <span
              className={`ml-3 text-sm ${
                progressStep >= 3 ? 'text-gray-900' : 'text-gray-500'
              } font-baijamjuree font-medium`}
            >
              Submit
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      {closed ? (
        <SubmissionClosedState closeDate={closeDate} />
      ) : isSubmitting ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingState message="Submitting your application..." />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <ErrorState
            title="Submission Error"
            error={error}
            onRetry={handleFormSubmit}
          />
        </div>
      ) : (
        <SubmissionForm
          selectedCategory={selectedCategory}
          submitted={submitted}
          error={error}
          isSubmitting={isSubmitting}
          rubricData={rubricData}
          categories={categories}
          handleCategoryChange={handleCategoryChange}
          handleFormSubmit={handleFormSubmit}
          dynamicFormSchema={dynamicFormSchema}
        />
      )}
    </PageLayout>
  );
};

export default SMDDFormPage;
